import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Box,
  Tabs,
  Tab,
  IconButton,
  Typography,
  LinearProgress,
  Button,
  Chip,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PropTypes from "prop-types";
import { API_get_calendar_messages_tomorrow } from "./utils/http";
import dayjs from "dayjs";
import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function CalendarMess() {
  const [value, setValue] = useState(1);

  //   call api
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function fetch_data(query_string) {
    await API_get_calendar_messages_tomorrow(query_string, (data) => {
      setData(data.data);
      setLoading(false);
    });
  }

  const copyToClipboard = (text) => {
    try {
      if (!navigator.clipboard) {
        fallbackCopyTextToClipboard(text);
      } else {
        navigator.clipboard
          .writeText(text)
          .then(() => {})
          .catch((err) => {
            console.error("Could not copy text: ", err);
            fallbackCopyTextToClipboard(text);
          });
      }
    } catch (err) {
      console.error("Clipboard error: ", err);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Make the textarea out of viewport
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      const msg = successful ? "successful" : "unsuccessful";
      console.log("Fallback: Copying text was " + msg);
      if (!successful) {
      }
    } catch (err) {
      console.error("Fallback: Could not copy text: ", err);
    }

    document.body.removeChild(textArea);
  };

  useEffect(() => {
    fetch_data("tomorrow");
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  if (loading) {
    return <LinearProgress />;
  }
  if (error) {
    return <Typography>Error loading data</Typography>;
  }
  if (data.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography style={{ fontSize: 15, color: "#999" }}>
          No messages found for this date
        </Typography>
      </Box>
    );
  }
  if (data.length > 0) {
    return (
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", px: 2 }}>
        {/* Header bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 17 }}>
              Calendar Messages
            </Typography>
            <Chip
              label={`${data.length} booking${data.length !== 1 ? "s" : ""}`}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: 12,
                backgroundColor: "#EBF4FF",
                color: "#1565C0",
                border: "none",
              }}
            />
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13,
            }}
            onClick={() => {
              const queries = ["today", "tomorrow", "next_2_days", "next_3_days", "next_4_days"];
              fetch_data(queries[value] || "tomorrow");
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            borderBottom: "1px solid #E0E0E0",
            mb: 2,
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                fontSize: 13,
                minHeight: 42,
              },
            }}
          >
            <Tab
              label={`Today (${dayjs().format("ddd DD/MM")})`}
              {...a11yProps(0)}
              onClick={() => fetch_data("today")}
            />
            <Tab
              label={`Tomorrow (${dayjs().add(1, "day").format("ddd DD/MM")})`}
              {...a11yProps(1)}
              onClick={() => fetch_data("tomorrow")}
            />
            <Tab
              label={dayjs().add(2, "day").format("ddd DD/MM")}
              {...a11yProps(2)}
              onClick={() => fetch_data("next_2_days")}
            />
            <Tab
              label={dayjs().add(3, "day").format("ddd DD/MM")}
              {...a11yProps(3)}
              onClick={() => fetch_data("next_3_days")}
            />
            <Tab
              label={dayjs().add(4, "day").format("ddd DD/MM")}
              {...a11yProps(4)}
              onClick={() => fetch_data("next_4_days")}
            />
          </Tabs>
        </Box>

        {/* Booking cards */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pb: 3 }}>
          {data.map((item) => (
            <Card
              key={item._id}
              elevation={0}
              sx={{
                borderRadius: "10px",
                border: "1px solid #E0E0E0",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                overflow: "hidden",
                transition: "box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
                },
              }}
            >
              {/* Card header */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.2,
                  backgroundColor: "#F5F7FA",
                  borderBottom: "1px solid #E8E8E8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{ fontWeight: 600, fontSize: 15, color: "#1565C0" }}
                >
                  {item.booking_number}
                </Typography>
                <Chip
                  label={item.day_index}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    backgroundColor: "#1976D2",
                    color: "#fff",
                    height: 24,
                  }}
                />
              </Box>

              {/* Card content */}
              <CardContent
                sx={{
                  p: 2.5,
                  "&:last-child": { pb: 2.5 },
                  display: "flex",
                  gap: 3,
                }}
              >
                {/* Left: booking info */}
                <Box
                  sx={{
                    width: "38%",
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {[
                    { label: "Customer", value: item.customer_name },
                    {
                      label: "Phone",
                      value:
                        item.customer_phone === "TBA"
                          ? "—"
                          : item.customer_phone,
                    },
                    { label: "Itinerary", value: item.activity_label },
                    { label: "Activities", value: item.activity_detail },
                    { label: "Accom", value: item.accommodation_label },
                  ].map((row) => (
                    <Box
                      key={row.label}
                      sx={{ display: "flex", alignItems: "baseline", gap: 1 }}
                    >
                      <Typography
                        sx={{
                          color: "#999",
                          fontWeight: 600,
                          minWidth: 70,
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          fontSize: 11,
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Divider orientation="vertical" flexItem sx={{ borderColor: "#EEEEEE" }} />

                {/* Right: message */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#999",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                        fontSize: 11,
                      }}
                    >
                      Message
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(item.message_tool)}
                      sx={{
                        backgroundColor: "#F0F7FF",
                        color: "#1976D2",
                        width: 30,
                        height: 30,
                        "&:hover": { backgroundColor: "#DDEEFF" },
                      }}
                    >
                      <ContentCopyTwoToneIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  <Typography
                    sx={{
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.65,
                      fontSize: 13,
                      color: "#333",
                    }}
                  >
                    {item.message_tool}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  }
}
